import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, saveSession, getStoredToken, getStoredUser, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import LangToggle from '@/components/LangToggle';

const HERO_BG = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080';

export default function Login() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [mode, setMode] = useState('email');
  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const route = (u) => {
    if (u.role === 'admin') nav('/coach/clients', { replace: true });
    else if (!u.onboarding_done) nav('/onboarding', { replace: true });
    else nav('/app/home', { replace: true });
  };

  useEffect(() => {
    const token = getStoredToken();
    const user = getStoredUser();
    if (token && user) route(user);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('pw_changed')) {
      sessionStorage.removeItem('pw_changed');
      // Defer so the Sonner Toaster has mounted/subscribed after the hard redirect.
      // No cleanup on purpose: under React.StrictMode the effect double-invokes and a
      // cleanup would cancel this timeout before it can fire.
      setTimeout(() => toast.success(t('pw.success')), 300);
    }
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!credential || !password) return toast.error(t('common.fillAll'));
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { credential, password });
      saveSession(res.data.token, res.data.user);
      route(res.data.user);
    } catch (err) {
      toast.error(errMsg(err, t('common.tryAgain')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-asgym-bgdeep">
      <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,9,11,0.4) 0%, rgba(8,9,11,0.88) 55%, #08090B 100%)' }} />

      <LangToggle className="absolute right-5 top-5 z-20" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col justify-between px-6 py-10">
        <div className="mt-6 flex flex-col items-center">
          <div className="text-center">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/as-gym-logo.png`}
              alt="A'S GYM"
              data-testid="login-logo"
              className="mx-auto h-52 w-auto max-w-[82%] object-contain mix-blend-screen sm:h-60"
            />
            <div className="mx-auto my-4 h-px w-8 bg-white/20" />
            <p className="text-[11px] font-semibold tracking-[5px] text-asgym-sub">{t('login.brandSub')}</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="mt-10 rounded-3xl border border-white/[0.06] bg-[#16171A]/80 p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl animate-fade-up"
          style={{ animationDelay: '0.1s' }}
        >
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-white">{t('login.welcomeBack')}</h2>
          <p className="mb-6 mt-1.5 text-[13px] text-asgym-sub">{t('login.signIn')}</p>

          <div className="mb-6 flex rounded-xl bg-black/40 p-1">
            {['email', 'phone'].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`login-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 rounded-lg py-3 text-[11px] font-bold tracking-[1.5px] transition-colors ${
                  mode === m ? 'bg-asgym-elevated text-white' : 'text-asgym-muted'
                }`}
              >
                {m === 'email' ? t('login.tabEmail') : t('login.tabPhone')}
              </button>
            ))}
          </div>

          <label className="as-label">{mode === 'email' ? t('login.email') : t('login.phone')}</label>
          <input
            data-testid="login-credential"
            className="as-input mt-2"
            type={mode === 'email' ? 'email' : 'tel'}
            autoCapitalize="none"
            placeholder={mode === 'email' ? t('login.placeholderEmail') : t('login.placeholderPhone')}
            value={credential}
            onChange={(e) => setCredential(e.target.value)}
          />

          <label className="as-label mt-4 block">{t('login.password')}</label>
          <div className="mt-2 flex items-center rounded-xl border border-white/[0.06] bg-black/30 transition-colors focus-within:border-white/20">
            <input
              data-testid="login-password"
              className="h-14 flex-1 bg-transparent px-4 text-[15px] text-white placeholder:text-asgym-muted outline-none"
              type={showPw ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((s) => !s)} className="px-4 text-asgym-sub">
              {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="button"
            data-testid="goto-forgot-password"
            onClick={() => nav('/forgot-password')}
            className="mt-3 block w-full text-right text-[13px] text-asgym-sub transition-colors hover:text-white"
          >
            {t('login.forgot')}
          </button>

          <button type="submit" data-testid="login-submit" disabled={loading} className="as-btn-primary mt-8 w-full">
            {loading ? <Loader2 className="animate-spin" size={20} /> : t('login.submit')}
          </button>

          <button type="button" data-testid="goto-register" onClick={() => nav('/register')} className="mt-5 w-full text-center text-[13px] text-asgym-sub">
            {t('login.noAccount')} <span className="font-bold text-white">{t('login.register')}</span>
          </button>
        </form>

        <div className="mt-8 text-center text-[11px] text-asgym-muted">
          Demo: demo@asgym.com · Demo123!
        </div>
      </div>
    </div>
  );
}
